import { defineStore } from "pinia";
import { ref } from "vue";
import { useRouter } from "vue-router";

import { isArray, GENERAL_ROUTE_NAMES, TARIFF_ROUTE_NAMES } from "@/shared";

import { getActiveTariff } from "../api/getActiveTariff";
import { getTariffsList } from "../api/getTariffsList";

export const useTariffStore = defineStore("tariffStore", () => {
    const router = useRouter();

    const tariff = ref(null);
    const statistic = ref(null);
    const isFree = ref(false);
    const referral = ref(null);
    const autopayment = ref(false);
    const hasPaymentMethod = ref(false);
    const tariffsList = ref(null);
    const freeTariff = ref(null);

    // Deduplicate concurrent active-tariff requests and reuse loaded tariff unless forced.
    let pendingPromise = null;

    const setTariff = async ({ force = false } = {}) => {
        if (pendingPromise) {
            return pendingPromise;
        }

        const routeName = router.currentRoute.value?.name;
        const isPaymentRoute = [
            GENERAL_ROUTE_NAMES.BUY_TARIFF,
            TARIFF_ROUTE_NAMES.PAY,
        ].includes(routeName);

        if (tariff.value !== null && !force && !isPaymentRoute) {
            return tariff.value;
        }

        pendingPromise = (async () => {
            try {
                const {
                    tariff: currentTariff,
                    statistic: currentStatistic,
                    isFree: currentIsFree,
                    referral: currentReferral,
                    autopayment: currentAutopayment,
                    hasPaymentMethod: currentHasPaymentMethod,
                } = await getActiveTariff();

                tariff.value = currentTariff;
                statistic.value = currentStatistic;
                isFree.value = currentIsFree;
                referral.value = currentReferral;
                autopayment.value = currentAutopayment;
                hasPaymentMethod.value = currentHasPaymentMethod;

                return currentTariff;
            } finally {
                pendingPromise = null;
            }
        })();

        return pendingPromise;
    };

    const setStatistic = (s) => {
        statistic.value = s;

        if (s.daysLeft === 0) {
            router.push({ name: GENERAL_ROUTE_NAMES.BUY_TARIFF });
        }
    };

    const recalculateTariffsWithBonuses = (isUseBonus) => {
        if (
            isUseBonus &&
            isArray(tariffsList.value) &&
            referral.value.bonus > 0
        ) {
            tariffsList.value.forEach(({ children }) => {
                children.forEach((child) => {
                    if (referral.value.bonus > child.cost) {
                        child.cost -=
                            referral.value.bonus -
                            (referral.value.bonus - child.cost + 1);
                    } else {
                        child.cost -= referral.value.bonus;
                    }
                });
            });
        }
    };

    const fetchTariffsList = async (promocode, isUseBonus = false) => {
        const { tariffs, freeTariff: free } = await getTariffsList(promocode);

        tariffsList.value = tariffs;
        freeTariff.value = free;

        if (isUseBonus) {
            recalculateTariffsWithBonuses(isUseBonus);
        }
    };

    return {
        tariff,
        statistic,
        tariffsList,
        freeTariff,
        isFree,
        referral,
        autopayment,
        hasPaymentMethod,
        setTariff,
        setStatistic,
        fetchTariffsList,
        recalculateTariffsWithBonuses,
    };
});
